// src/pages/cadastros/Empresas/EmpresasPage.jsx
// (Mamute Fitness) UI rebatizado para "Clientes".
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Can } from "../../../components/Can";

import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useIsMobile } from "../../../hooks/useIsMobile";
import LayoutDesktop from "../../../layouts/LayoutDesktop";
import LayoutMobile from "../../../layouts/LayoutMobile";
import FiltroBar from "../../../components/FiltroBar/FiltroBar";
import CardListMobile from "../../../templates/mobile/CardListMobile";
import {
  IconDocument,
  IconLocation,
  IconPhone,
  IconEmail,
  IconUser,
} from "../../../components/Icons/Icons";
import { useSortableTable, SortableHeader } from "../../../hooks/useSortableTable";
import { nivelUsuario, pode } from "../../../utils/permissoes";
import { excluirClienteComCascata } from "../../../services/excluirClienteCascadeApi";

import { db } from "../../../config/firebase";
import {
  collection,
  getDocs,
  query,
  where,
  documentId,
} from "firebase/firestore";

function renderCodigo(cliente) {
  if (cliente.codigo) return cliente.codigo;
  if (cliente.codigoSequencial != null) {
    return `CL${String(cliente.codigoSequencial).padStart(4, "0")}`;
  }
  if (cliente.id) return cliente.id.slice(0, 8);
  return "-";
}

function EmpresasPage() {
  const { tema, alterarTema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile(900);

  const [clientes, setClientes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [sidebarAberta, setSidebarAberta] = useState(false);

  const [campoFiltro, setCampoFiltro] = useState("nome");
  const [textoFiltro, setTextoFiltro] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const canExcluir = pode(usuarioLogado, "cadastros", "empresas", "deleta");

  const camposFiltro = [
    { value: "nome", label: "Nome" },
    { value: "responsavel", label: "Responsável" },
    { value: "telefone", label: "Telefone" },
    { value: "email", label: "Email" },
    { value: "tipo", label: "Tipo" },
    { value: "endereco", label: "Endereço" },
    { value: "codigo", label: "Código" },
  ];

  useEffect(() => {
    async function carregar() {
      if (!usuarioLogado) return;

      setCarregando(true);
      setErro("");

      try {
        const empresasRef = collection(db, "empresas");

        const nUser = nivelUsuario(usuarioLogado);
        const isAdminLike = nUser >= 5; // Administrador/Gestor
        const isGlobal =
          usuarioLogado?.globalAccess === true ||
          usuarioLogado?.acessoGlobal === true;

        // ✅ Importante: pelas regras do Firestore, usuário não-admin/global NÃO consegue listar todas as empresas do grupo
        // via where(grupoId==...), pois a query pode "bater" em empresas não permitidas e o Firestore bloqueia a consulta.
        // Então, para não-admin/global, buscamos SOMENTE as empresas permitidas (documentId in [...] / em lotes de 10).
        let lista = [];

        if (isAdminLike || isGlobal) {
          let q;
          if (usuarioLogado.grupoId) {
            q = query(empresasRef, where("grupoId", "==", usuarioLogado.grupoId));
          } else {
            q = empresasRef;
          }
          const snap = await getDocs(q);
          lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        } else {
          const idsSet = new Set();

          const addId = (v) => {
            if (typeof v === "string") {
              const s = v.trim();
              if (s) idsSet.add(s);
            }
          };

          // compat: empresaId atual e listas antigas
          addId(usuarioLogado?.empresaId);
          addId(usuarioLogado?.empresaID);
          (Array.isArray(usuarioLogado?.empresasPermitidas)
            ? usuarioLogado.empresasPermitidas
            : []
          ).forEach(addId);
          (Array.isArray(usuarioLogado?.empresasAcesso)
            ? usuarioLogado.empresasAcesso
            : []
          ).forEach(addId);

          const ids = Array.from(idsSet);

          if (ids.length > 0) {
            const chunks = [];
            for (let i = 0; i < ids.length; i += 10) chunks.push(ids.slice(i, i + 10));

            const snaps = await Promise.all(
              chunks.map((ch) =>
                getDocs(query(empresasRef, where(documentId(), "in", ch)))
              )
            );

            const map = new Map();
            snaps.forEach((s) => {
              s.docs.forEach((d) => map.set(d.id, { id: d.id, ...d.data() }));
            });

            lista = Array.from(map.values());
          } else {
            lista = [];
          }
        }

        const baseId = usuarioLogado?.empresaId || usuarioLogado?.empresaID || null;
        const baseNomeRaw =
          usuarioLogado?.empresaNome ||
          usuarioLogado?.empresa ||
          usuarioLogado?.grupoNome ||
          "";
        const baseNome = String(baseNomeRaw).trim().toLowerCase();

        const listaFiltrada = lista.filter((item) => {
          if (baseId && item.id === baseId) return false;
          if (!baseNome) return true;
          return String(item.nome || "").trim().toLowerCase() !== baseNome;
        });

        setClientes(listaFiltrada);
      } catch (e) {
        console.error("Erro ao carregar clientes:", e);
        setErro("Erro ao carregar clientes. Tente novamente.");
      } finally {
        setCarregando(false);
      }
    }

    carregar();
  }, [usuarioLogado]);

  const clientesFiltrados = clientes.filter((cliente) => {
    const texto = textoFiltro.trim().toLowerCase();
    if (!texto) return true;

    let valorBase = "";

    if (campoFiltro === "codigo") {
      valorBase = renderCodigo(cliente);
    } else {
      valorBase = cliente[campoFiltro] ?? "";
    }

    return String(valorBase).toLowerCase().includes(texto);
  });

  const { sortedData, requestSort, getSortDirection } = useSortableTable(
    clientesFiltrados,
    "nome"
  );

  async function handleExcluir(clienteId) {
    if (!clienteId) return;

    const confirmar = window.confirm(
      "Tem certeza que deseja excluir este cliente? Essa ação não pode ser desfeita."
    );
    if (!confirmar) return;

    setCarregando(true);
    setErro("");

    try {
      await excluirClienteComCascata({
        grupoId: usuarioLogado?.grupoId || null,
        clienteId,
      });
      setClientes((prev) => prev.filter((e) => e.id !== clienteId));
    } catch (e) {
      console.error("Erro ao excluir cliente:", e);
      setErro("Erro ao excluir cliente. Tente novamente.");
    } finally {
      setCarregando(false);
    }
  }

  const itemsParaCards = clientesFiltrados.map((c) => ({
    id: c.id,
    nome: c.nome || "Sem nome",
    subtitulo: renderCodigo(c),
    detalhes: [
      { icone: <IconUser size="14px" />, label: "Responsável", valor: c.responsavel || "-" },
      { icone: <IconPhone size="14px" />, label: "Telefone", valor: c.telefone || "-" },
      { icone: <IconEmail size="14px" />, label: "Email", valor: c.email || "-" },
      { icone: <IconDocument size="14px" />, label: "Tipo", valor: c.tipo || "-" },
      { icone: <IconLocation size="14px" />, label: "Endereço", valor: c.endereco || "-" },
    ],
  }));

  if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        alterarTema={alterarTema}
        titulo="Clientes"
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
        abaBottom="cadastros"
      >
        <CardListMobile
          items={itemsParaCards}
          carregando={carregando}
          erro={erro}
          carregandoMessage="Carregando clientes..."
          titulo="Clientes"
          searchPlaceholder="Buscar cliente..."
          novoLabel="Novo Cliente"
          emptyMessage="Nenhum cliente encontrado"
          onHome={() => navigate("/")}
          onNovo={() => navigate("/cadastros/clientes/novo")}
          onEditar={(id) => navigate(`/cadastros/clientes/editar/${id}`)}
          onExcluir={canExcluir ? handleExcluir : undefined}
          getSearchText={(item) => `${item.nome} ${item.subtitulo}`}
        />
      </LayoutMobile>
    );
  }

  const empresaBaseNome =
    usuarioLogado?.grupoNome ||
    usuarioLogado?.empresaNome ||
    usuarioLogado?.empresa ||
    "Empresa base não definida";

  return (
    <LayoutDesktop tema={tema} titulo="Clientes" subtitulo="">
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Clientes cadastrados</h3>
            <p>Lista de clientes vinculados a empresa base do usuário logado.</p>
          </div>

          <div className="pc-table-header-actions">
            <Can modulo="cadastros" recurso="empresas" acao="form">
              <button
                type="button"
                className="pc-btn-primario"
                onClick={() => navigate("/cadastros/clientes/novo")}
              >
                + Novo Cliente
              </button>
            </Can>
          </div>
        </header>

        <div
          style={{
            marginBottom: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <p style={{ fontSize: 14 }}>
            <strong>Empresa base:</strong> {empresaBaseNome}
          </p>
        </div>

        <FiltroBar
          campos={camposFiltro}
          campoSelecionado={campoFiltro}
          onCampoChange={setCampoFiltro}
          textoFiltro={textoFiltro}
          onTextoChange={setTextoFiltro}
          onAdicionarClick={() => navigate("/cadastros/clientes/novo")}
          placeholder="Filtrar clientes..."
          mostrarAdicionar={false}
        />

        <div className="pc-table-wrapper">
          {erro && (
            <div
              style={{
                background: "#7f1d1d",
                color: "#fecaca",
                padding: 8,
                borderRadius: 8,
                marginBottom: 12,
                fontSize: 14,
              }}
            >
              {erro}
            </div>
          )}

          {carregando ? (
            <p style={{ padding: 10 }}>Carregando clientes...</p>
          ) : sortedData.length === 0 ? (
            <p style={{ padding: 10 }}>Nenhum cliente encontrado.</p>
          ) : (
            <table className="pc-table pc-table-expandable">
              <thead>
                <tr>
                  <th style={{ width: 44 }} />
                  <SortableHeader
                    label="Nome"
                    sortKey="nome"
                    onSort={requestSort}
                    sortDirection={getSortDirection("nome")}
                  />
                  <SortableHeader
                    label="Responsável"
                    sortKey="responsavel"
                    onSort={requestSort}
                    sortDirection={getSortDirection("responsavel")}
                  />
                  <SortableHeader
                    label="Telefone"
                    sortKey="telefone"
                    onSort={requestSort}
                    sortDirection={getSortDirection("telefone")}
                  />
                  <SortableHeader
                    label="Tipo"
                    sortKey="tipo"
                    onSort={requestSort}
                    sortDirection={getSortDirection("tipo")}
                  />
                  <SortableHeader
                    label="Email"
                    sortKey="email"
                    onSort={requestSort}
                    sortDirection={getSortDirection("email")}
                  />
                  <th style={{ textAlign: "right" }}>Ações</th>
                </tr>
              </thead>

              <tbody>
                {sortedData.map((cliente) => {
                  const aberto = expandedId === cliente.id;

                  return (
                    <React.Fragment key={cliente.id}>
                      <tr className={aberto ? "pc-row-open" : undefined}>
                        <td>
                          <button
                            type="button"
                            className="pc-expand-btn"
                            aria-label={aberto ? "Recolher" : "Expandir"}
                            onClick={() =>
                              setExpandedId((prev) => (prev === cliente.id ? null : cliente.id))
                            }
                          >
                            <span className={aberto ? "pc-expand-chevron open" : "pc-expand-chevron"}>
                              ▸
                            </span>
                          </button>
                        </td>

                        <td style={{ fontWeight: 600 }}>{cliente.nome || "-"}</td>
                        <td>{cliente.responsavel || "-"}</td>
                        <td>{cliente.telefone || "-"}</td>
                        <td>{cliente.tipo || "-"}</td>
                        <td>{cliente.email || "-"}</td>

                        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                          <Can modulo="cadastros" recurso="empresas" acao="altera">
                            <button
                              type="button"
                              className="pc-icon-btn"
                              title="Editar cliente"
                              onClick={() => navigate(`/cadastros/clientes/editar/${cliente.id}`)}
                            >
                              ✏️
                            </button>
                          </Can>

                          <Can modulo="cadastros" recurso="empresas" acao="deleta">
                            <button
                              type="button"
                              className="pc-icon-btn"
                              title="Apagar cliente"
                              onClick={() => handleExcluir(cliente.id)}
                            >
                              🗑️
                            </button>
                          </Can>
                        </td>
                      </tr>

                      {aberto && (
                        <tr className="pc-expanded-row">
                          <td colSpan={7}>
                            <div className="pc-expand-panel">
                              <div className="pc-expand-grid">
                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Endereço</div>
                                  <div className="pc-expand-value">{cliente.endereco || "-"}</div>
                                </div>

                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Código</div>
                                  <div className="pc-expand-value">{renderCodigo(cliente)}</div>
                                </div>

                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Tipo</div>
                                  <div className="pc-expand-value">{cliente.tipo || "-"}</div>
                                </div>

                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">Empresa base</div>
                                  <div className="pc-expand-value">
                                    {cliente.grupoNome || cliente.grupoId || "-"}
                                  </div>
                                </div>

                                <div className="pc-expand-item">
                                  <div className="pc-expand-label">ID</div>
                                  <div className="pc-expand-value">{cliente.id}</div>
                                </div>
                              </div>

                              <div className="pc-expand-actions">
                                <button
                                  type="button"
                                  className="pc-btn-secundario"
                                  onClick={() => navigate(`/cadastros/clientes/editar/${cliente.id}`)}
                                >
                                  Editar
                                </button>

                                <Can modulo="cadastros" recurso="empresas" acao="deleta">
                                  <button
                                    type="button"
                                    className="pc-btn-perigo"
                                    onClick={() => handleExcluir(cliente.id)}
                                  >
                                    Excluir
                                  </button>
                                </Can>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </LayoutDesktop>
  );
}

export default EmpresasPage;
